const fetchJson = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch response from ${url}`);
  }
  return response.json();
};

export const formatDateForBlob = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;  // DD-MM-YYYY
};

export const getRecentDateStrings = (n = 4) => {
  const today = new Date();
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    return formatDateForBlob(d);
  });
};

export const fetchStockDataForDate = async (dateStr) => {
  const ch14 = `https://investingfunctionstorage.blob.core.windows.net/alphavantage/stock_data_big_usa_${dateStr}.json`;
  const ch15 = `https://investingfunctionstorage.blob.core.windows.net/alphavantage/stock_data_small_usa_${dateStr}.json`;
  const sp500 = `https://investingfunctionstorage.blob.core.windows.net/alphavantage/stock_data_sp500_${dateStr}.json`;

  const [stockdata, morestockdata, sp500data] = await Promise.all([
    fetchJson(ch14),
    fetchJson(ch15),
    fetchJson(sp500),
  ]);

  return {
    combined: stockdata.concat(morestockdata, sp500data),
  };
};

export const fetchStockDataWithFallback = async (dateStrings = []) => {
  let lastError = null;

  for (const dateStr of dateStrings) {
    try {
      const payload = await fetchStockDataForDate(dateStr);
      return { ...payload, dateStr };
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error("No dates provided for fetching stock data.");
};

const createChapterHighlightDefault = () => ({
  ch14Success: false,
  ch15Success: false,
});

const parseFilterResultsDate = (value = "") => {
  const parts = typeof value === "string" ? value.split("-") : [];
  if (parts.length !== 3) {
    return null;
  }

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);

  if (!day || !month || !year) {
    return null;
  }

  return new Date(year, month - 1, day);
};

const getLatestFilterRecord = (history = []) =>
  history.reduce((latest, record) => {
    if (!record || !record.date) {
      return latest;
    }

    if (!latest) {
      return record;
    }

    const recordDate = parseFilterResultsDate(record.date);
    const latestDate = parseFilterResultsDate(latest.date);

    if (!recordDate) {
      return latest;
    }
    if (!latestDate || recordDate > latestDate) {
      return record;
    }
    return latest;
  }, null);

const startsWithSuccess = (text = "") =>
  typeof text === "string" && text.trim().toUpperCase().startsWith("SUCCESS");

export const fetchLatestFilterHistory = async (dateStr, ticker) => {
  const normalizedTicker = (ticker || "").toLowerCase();

  if (!normalizedTicker) {
    return null;
  }

  const candidateDates = new Set();
  if (dateStr) {
    candidateDates.add(dateStr);

    const parsed = parseFilterResultsDate(dateStr);
    if (parsed) {
      for (let offset = 1; offset <= 2; offset += 1) {
        const backDate = new Date(parsed);
        backDate.setDate(parsed.getDate() - offset);
        candidateDates.add(formatDateForBlob(backDate));
      }
    }
  }

  if (candidateDates.size === 0) {
    getRecentDateStrings(4).forEach((d) => candidateDates.add(d));
  }

  for (const candidate of candidateDates) {
    const url = `https://investingfunctionstorage.blob.core.windows.net/alphavantage/filter_results_${candidate}.json`;

    try {
      const response = await fetchJson(url);
      const stocks =
        response && Array.isArray(response.stockdata) ? response.stockdata : [];

      const entry = stocks.find(
        (stock) => (stock.ticker || "").toLowerCase() === normalizedTicker
      );

      if (!entry || !Array.isArray(entry.history) || entry.history.length === 0) {
        continue;
      }

      return getLatestFilterRecord(entry.history);
    } catch (err) {
      console.warn(
        `Unable to fetch latest filter history for ${candidate}:`,
        err
      );
    }
  }

  return null;
};

export const fetchChapterHighlights = async (dateStr, tickers = []) => {
  const normalizedTickers = Array.from(
    new Set(
      tickers
        .map((ticker) => (ticker || "").toLowerCase())
        .filter((ticker) => ticker.length > 0)
    )
  );

  const highlightMap = {};
  normalizedTickers.forEach((ticker) => {
    highlightMap[ticker] = createChapterHighlightDefault();
  });

  if (!dateStr || normalizedTickers.length === 0) {
    return highlightMap;
  }

  const url = `https://investingfunctionstorage.blob.core.windows.net/alphavantage/filter_results_${dateStr}.json`;

  try {
    const response = await fetchJson(url);
    const stocks =
      response && Array.isArray(response.stockdata) ? response.stockdata : [];

    normalizedTickers.forEach((ticker) => {
      const entry = stocks.find(
        (stock) => (stock.ticker || "").toLowerCase() === ticker
      );

      if (!entry || !Array.isArray(entry.history) || entry.history.length === 0) {
        highlightMap[ticker] = createChapterHighlightDefault();
        return;
      }

      const latestRecord = getLatestFilterRecord(entry.history);

      if (!latestRecord || !latestRecord.detail) {
        highlightMap[ticker] = createChapterHighlightDefault();
        return;
      }

      highlightMap[ticker] = {
        ch14Success: startsWithSuccess(
          latestRecord.detail["Chapter 14 Analysis"] || ""
        ),
        ch15Success: startsWithSuccess(
          latestRecord.detail["Chapter 15 Analysis"] || ""
        ),
      };
    });
  } catch (err) {
    console.warn("Unable to fetch chapter highlights:", err);
  }

  return highlightMap;
};

export { fetchJson };
